/**
 * @file        src/pages/payment/InvoiceSeriesSettings.jsx
 * @description Invoice Number Series (CR B6) — one editable row per
 *              (invoice_type, brand): prefix, starting number, zero-padding,
 *              financial-year reset. Onboarding-time config the client
 *              expects to set once and rarely touch again. start_number only
 *              takes effect for a series/FY that hasn't issued a number yet —
 *              the form explains this rather than implying a live renumber.
 * @author      Kinetika Labs
 * @created     2026-08-04
 * @changelog
 *   - 2026-09-22 Claude — The header no longer claims a year the invoices do not
 *                          have. "Invoices issued now carry 2026-27" was printed
 *                          unconditionally, while 067_invoice_series_fy_default.sql
 *                          deliberately leaves a series that has ALREADY ISSUED
 *                          numbers un-FY-numbered (switching it on mid-year restarts
 *                          the counter — a discontinuity that is the client's call,
 *                          not a migration's). So a tenant already at NHONF-000145
 *                          read a financial year on this screen and none on the
 *                          document, with nothing on the page explaining the gap.
 *                          It now states how many series actually carry the year and
 *                          what to do about the ones that do not. The per-row chip
 *                          ("Not FY-numbered") and the samples were already correct.
 *   - 2026-09-22 Claude — THE FINANCIAL YEAR IS ON THE PAGE, not only inside the
 *                          edit modal, and the sample is REAL. The list showed no
 *                          FY at all, and the modal's preview hardcoded the string
 *                          '2026-27' — so it would have gone on claiming 2026-27
 *                          for ever, and it previewed start_number even for a
 *                          series already at 412. Every sample on this screen now
 *                          comes from the SERVER (invoiceSeries.service builds it
 *                          with the same formatter booking_backend allocates with),
 *                          so what is shown is the string the next invoice will
 *                          carry — not a second guess at the format.
 *                          The header states the open financial year, the exact IST
 *                          instant numbering restarts, and what the first number of
 *                          the next year will be.
 *   - 2026-08-10 Claude — Fixed the Preview row (and, latently, the Prefix/
 *                          Separator/Zero-padding row) visually spilling past
 *                          the modal's right edge: `.field{min-width:160px}` ×3
 *                          columns (480px) plus gaps exceeds this modal's
 *                          460px max-width, and CSS Grid's default `1fr`
 *                          tracks don't shrink below that floor — the excess
 *                          paints outside the modal since nothing clips it.
 *                          Scoped via a page-local `.invsr-modal` class (see
 *                          admin.css) rather than touching the shared
 *                          `.lk-modal`/`.field` rules ~25 other modals use.
 *   - 2026-08-06 Claude — 'insurance' added (043_invoice_series_insurance.sql).
 *   - 2026-08-04 Claude — Initial (Invoice Number Series settings).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import TableSkeleton from '../../components/TableSkeleton';
import Icon from '../../components/Icon';
import { get, put } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';

const TYPE_LABEL = { flight: 'Flight', hotel: 'Hotel', insurance: 'Insurance' };

/**
 * IST, always — the financial-year boundary is a fact about India, not about
 * where the person reading the screen happens to be sitting. An admin in Dubai
 * must see the same "1 Apr 2027, 12:00 am" the allocator switches on.
 */
const IST = 'Asia/Kolkata';
const istDateTime = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', {
  timeZone: IST, day: '2-digit', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: true,
}) : '');
const istDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', {
  timeZone: IST, day: '2-digit', month: 'short', year: 'numeric',
}) : '');
/**
 * The LAST DAY of a financial year, from the exclusive upper bound the API sends.
 * `fy_restarts_at` is the instant the next year's first number is issued —
 * 1 Apr 00:00 IST — so printing it raw as the end of the range said the year ran
 * "01 Apr 2026 to 01 Apr 2027", which overlaps the next year by a day and is not
 * what anyone means by a financial year. One millisecond back lands on 31 Mar.
 */
const istLastDay = (iso) => (iso ? istDate(new Date(new Date(iso).getTime() - 1).toISOString()) : '');

function EditModal({ row, onClose, onSaved }) {
  const [form, setForm] = useState({
    prefix: row.prefix, start_number: row.start_number, padding: row.padding,
    fy_reset: !!row.fy_reset, separator: row.separator,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  // The FY label and the number come from the SERVER row, never from a literal
  // here: this preview used to hardcode '2026-27' (wrong from 1 Apr 2027 onward)
  // and always previewed start_number, so a series already at 412 previewed 001.
  // Only prefix/separator/padding/fy_reset are live-edited, so only those are read
  // from the form.
  const previewFy = row.fy_label;
  const previewN = form.fy_reset ? (row.fy_reset ? row.next_number : row.start_number) : row.next_number;
  const pad = Math.max(1, Math.min(12, Number(form.padding) || 1));
  const sep = form.separator;
  const preview = `${form.prefix}${sep}${form.fy_reset ? previewFy + sep : ''}${String(previewN || 1).padStart(pad, '0')}`;
  // Turning FY numbering ON mid-year starts a NEW counter at the starting number,
  // so a series already issuing 000412 jumps back to 000001 inside one filing
  // period. That is a real accounting event, not a formatting change — say so.
  const midYearRestart = !row.fy_reset && form.fy_reset && row.issued_this_period;

  async function save() {
    setBusy(true); setErr('');
    try {
      await put(`/invoice-series/${row.invoice_type}/${row.brand}`, {
        prefix: form.prefix.trim(),
        start_number: Number(form.start_number),
        padding: Number(form.padding),
        fy_reset: !!form.fy_reset,
        separator: form.separator.trim() || '-',
      });
      toast.ok('Saved', `${TYPE_LABEL[row.invoice_type]} · ${row.brand} series updated.`);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error?.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass invsr-modal" style={{ maxWidth: 460 }}>
        <div className="lk-modal-head">
          <h3>{TYPE_LABEL[row.invoice_type]} invoices · {row.brand}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>
        <div className="form-grid">
          <div className="field">
            <label>Prefix</label>
            <input value={form.prefix} onChange={set('prefix')} maxLength={20} />
          </div>
          <div className="field">
            <label>Separator</label>
            <input value={form.separator} onChange={set('separator')} maxLength={4} />
          </div>
          <div className="field">
            <label>Zero-padding (digits)</label>
            <input type="number" min={1} max={12} value={form.padding} onChange={set('padding')} />
          </div>
          <div className="field full">
            <label>Starting number</label>
            <input type="number" min={1} value={form.start_number} onChange={set('start_number')} />
            <small className="muted">
              {row.id
                ? "Only applies once this series (or the next financial year, if reset is on) hasn't issued a number yet — it can never renumber an already-issued invoice."
                : "This series hasn't issued a number yet — this is where it starts."}
            </small>
          </div>
          <label className="field full" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" checked={form.fy_reset} onChange={set('fy_reset')} style={{ width: 'auto' }} />
            <span>Reset numbering every financial year <span className="muted">(Apr–Mar)</span></span>
          </label>
          {midYearRestart ? (
            <div className="field full">
              <small className="muted" style={{ color: 'var(--rose)' }}>
                This series has already issued <span className="mono">{row.sample}</span> worth of numbers
                this year. Switching financial-year numbering on now starts a fresh counter
                at <span className="mono">{row.start_number}</span> straight away — a restart in the
                middle of a filing period, not at year end.
              </small>
            </div>
          ) : null}
          <div className="field full">
            <label>Next invoice will be</label>
            <div className="mono invsr-preview" style={{ padding: '10px 13px', background: 'var(--field)', borderRadius: 11, border: '1px solid var(--stroke)' }}>
              {preview}
            </div>
            {form.fy_reset ? (
              <small className="muted">
                Restarts at <span className="mono">{`${form.prefix}${sep}${row.next_fy_label}${sep}${String(row.start_number || 1).padStart(pad, '0')}`}</span>
                {' '}on {istDateTime(row.fy_restarts_at) || '1 Apr, 12:00 am'} IST.
              </small>
            ) : (
              <small className="muted">One continuous series — it never restarts.</small>
            )}
          </div>
          {err ? <div className="field-error">{err}</div> : null}
          <div className="form-actions full">
            <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function InvoiceSeriesSettings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: qk.invoiceSeries.list,
    queryFn: () => get('/invoice-series'),
  });
  const rows = data?.data || [];
  // Every row carries the same calendar (see invoiceSeries.service) — read it off
  // the first one rather than computing a second copy of the FY rule in the SPA.
  const fy = rows[0] || null;
  // How many series ACTUALLY put the year in their numbers. The open financial
  // year is a fact about the calendar and is stated either way, but "invoices
  // issued now carry 2026-27" is only true of a series with fy_reset on — said
  // unconditionally it describes numbers no invoice has, which is exactly how a
  // screen and a printed document come to disagree.
  const fyOn = rows.filter((r) => r.fy_reset).length;
  const [editRow, setEditRow] = useState(null);

  return (
    <>
      <PageHead title="Invoice Number Series" subtitle="Payment Management · gapless, per-brand invoice numbering" />

      {/* The financial year is the thing that was asked for, so it is stated on the
          page itself rather than only inside an edit modal — including the exact
          instant numbering rolls over, in IST, because that boundary is the whole
          point and it is NOT the reader's local midnight. `fy` comes from the API
          (every row carries the same calendar), so this can never drift from what
          the allocator does. */}
      {fy ? (
        <article className="panel glass" style={{ marginBottom: 16 }}>
          <div className="invsr-fy">
            <div>
              <span className="chip ok">FY {fy.fy_label}</span>
              <p className="muted" style={{ margin: '8px 0 0' }}>
                Open financial year — {istDate(fy.fy_starts_at)} to {istLastDay(fy.fy_restarts_at)} (IST).
                {' '}
                {fyOn === rows.length
                  ? <>Invoices issued now carry <span className="mono">{fy.fy_label}</span>.</>
                  : fyOn === 0
                    ? <>No series below is financial-year numbered, so invoice numbers carry
                        {' '}<b>no year</b> — each runs as one continuous sequence. Turn it on for a
                        series with Edit; a series that has already issued numbers restarts from its
                        starting number, which is why it was not switched on for you.</>
                    : <><b>{fyOn} of {rows.length}</b> series below are financial-year numbered and
                        carry <span className="mono">{fy.fy_label}</span>; the other
                        {' '}{rows.length - fyOn} run as one continuous sequence and carry no year.</>}
              </p>
            </div>
            <div>
              <span className="chip pend">Restarts {istDateTime(fy.fy_restarts_at)} IST</span>
              <p className="muted" style={{ margin: '8px 0 0' }}>
                At that moment every financial-year series below starts again from its
                own starting number, under <span className="mono">{fy.next_fy_label}</span>.
                Nothing to run and nothing to click — the counter is keyed on the year.
              </p>
            </div>
          </div>
        </article>
      ) : null}

      <article className="panel glass">
        {isLoading ? <TableSkeleton /> : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th><th>Brand</th><th>Prefix</th><th>Padding</th>
                  <th>Financial year</th><th>Starting number</th>
                  <th>Next invoice number</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.invoice_type}:${r.brand}`}>
                    <td>{TYPE_LABEL[r.invoice_type]}</td>
                    <td className="mono">{r.brand}</td>
                    <td className="mono">{r.prefix}{r.separator}</td>
                    <td>{r.padding} digits</td>
                    <td>
                      {r.fy_reset
                        ? <span className="chip ok">{r.fy_label}</span>
                        : <span className="chip warn" title="This series runs as one continuous sequence and never restarts.">Not FY-numbered</span>}
                    </td>
                    <td className="mono">{r.start_number}</td>
                    {/* THE SAMPLE IS THE REAL NEXT NUMBER, built server-side by the same
                        formatter booking_backend allocates with — not an illustration of
                        the format. `issued_this_period` is what separates "this is where
                        it starts" from "this is where it has got to". */}
                    <td>
                      <span className="mono">{r.sample}</span>
                      <br />
                      <small className="muted">
                        {r.issued_this_period
                          ? (r.fy_reset ? `in progress · restarts ${r.next_fy_label}` : 'in progress')
                          : 'first of this series'}
                      </small>
                    </td>
                    <td>
                      <button className="btn xs ghost" onClick={() => setEditRow(r)}>
                        <Icon name="edit" /> Edit
                      </button>
                    </td>
                  </tr>
                ))}
                {!rows.length ? (
                  <tr><td colSpan={8} className="empty">No brands registered yet.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </article>

      {editRow ? (
        <EditModal
          row={editRow}
          onClose={() => setEditRow(null)}
          onSaved={() => { setEditRow(null); qc.invalidateQueries({ queryKey: qk.invoiceSeries.list }); }}
        />
      ) : null}
    </>
  );
}
